import BasicLayout from '@components/layout/BasicLayout'
import Container from '@components/layout/Container'
import FullScreenSection from '@components/layout/FullScreenSection'
import PageSeo from '@components/seo/PageSeo'
import TrapezoidTitle from '@components/title/TrapezoidTitle'
import getDynasties from '@operations/queries/getDynasties'
import getSiteSettings from '@operations/queries/getSiteSettings'
import useMediaQuery, { maxWidthQueries, minWidthQueries } from '@operations/utils/useMediaQuery'
import Link from 'next/link'
import React from 'react'
import ProgressiveImage from 'react-progressive-image'
import styled from 'styled-components'

const GalleryTitle = styled.div`
  position: absolute;
  top: 4rem;
  left: 0;
  z-index: 6;
  width: 100%;
  @media ${minWidthQueries.md} {
    width: auto;
    left: 50%;
    transform: translateX(-50%);
    top: 6rem;
  }
`
const HeaderHolder = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`
const CustomHeader = styled.h1`
  justify-self: center;
  align-self: center;
  display: none;
  opacity: 0;
  transition: 0.5s;
  color: white;
  padding: 30px;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(16px);
  width: 50%;
  border-radius: 5px;
`
const GalleryWrap = styled.div`
  display: flex;
  width: 100%;
  height: auto;
  padding-top: 7rem;
  flex-direction: column;
  @media ${minWidthQueries.md} {
    flex-direction: row;
    padding-top: 0;
    height: 100vh;
  }
`
const GalleryItem = styled.a`
  display: block;
  background-position: center;
  background-size: cover;
  background-repeat: none;
  transition: flex 0.8s ease, box-shadow 0.8s ease;
  box-shadow: 0;
  background-image: ${({ bgImage }) => `url(${bgImage})`};
  filter: ${({ loading }) => (loading === 'true' ? 'blur(1px)' : '')};
  height: 250px;
  position: relative;
  &:after {
    content: 'More';
    position: absolute;
    bottom: 1rem;
    right: 1rem;
    color: white;
    text-decoration: underline;
    text-shadow: 2px 4px 3px rgb(0 0 0 / 30%);
    font-family: ${({ theme }) => theme.font.fontFamily.skyclimbers};
  }
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -7px;
    height: ${({ index }) => index * 15}%;
    width: 15px;
    background-color: ${({ theme }) => theme.colors.darkGrey};
    text-decoration: underline;
    text-shadow: 2px 4px 3px rgb(0 0 0 / 30%);
    font-family: ${({ theme }) => theme.font.fontFamily.skyclimbers};
    z-index: 5;
    clip-path: polygon(0% 0%, 0% 100%, 100% calc(100% - 15px), 100% 0%);
  }
  @media ${minWidthQueries.md} {
    flex: 1;
    height: 100%;
    &:after {
      content: '';
      position: absolute;
      bottom: 0;
      left: -7px;
      height: calc(99% - ${({ index }) => index * 15}%);
      width: 15px;
      background-color: ${({ theme }) => theme.colors.darkGrey};
      text-decoration: underline;
      text-shadow: 2px 4px 3px rgb(0 0 0 / 30%);
      font-family: ${({ theme }) => theme.font.fontFamily.skyclimbers};
      z-index: 5;
      clip-path: polygon(0% 100%, 100% 100%, 100% 0%, 0% 15px);
    }
    &:hover {
      flex: 7;
      box-shadow: 0px 14px 80px ${({ theme }) => theme.colors.black};
      position: relative;
      z-index: 1;
      ${CustomHeader} {
        display: block;
        opacity: 1;
        text-align: center;
      }
    }
  }
  &:first-child {
    &:before,
    &:after {
      content: none;
    }
  }
`

const Biome = ({ data: { dynasty, biomes, siteSettings, slug, dynastiesList } }) => {
  const isMobile = useMediaQuery(maxWidthQueries.sm)

  return (
    <BasicLayout
      dynastiesList={dynastiesList}
      footerLinks={siteSettings?.footerLinks}
      footerLogo={siteSettings?.footerLogo?.url}
      headerLinks={siteSettings?.headerLinks}
      headerLogo={siteSettings?.logo?.url}
      socialLinks={siteSettings?.socialLinks}
    >
      <PageSeo title={`${dynasty?.name || slug} Biomes | Sentipedia | Skyclimbers`} />
      <FullScreenSection marginTop="0">
        <Container justify="space-between" maxWidth="100vw" padding="0" position="relative">
          <GalleryTitle>
            <TrapezoidTitle title={`${dynasty?.name || slug} Biomes`} />
          </GalleryTitle>
          <GalleryWrap>
            {biomes?.map((biome, index) => (
              <ProgressiveImage
                key={biome.id}
                placeholder={biome.cover?.formats?.thumbnail?.url}
                src={isMobile ? biome.cover?.formats?.small?.url : biome.cover?.formats?.large?.url}
              >
                {(src, loading) => (
                  <Link passHref href={`/biomes/${biome.slug}`}>
                    <GalleryItem bgImage={src} index={index} loading={loading}>
                      <HeaderHolder>
                        <CustomHeader>{biome?.name || biome.slug}</CustomHeader>
                      </HeaderHolder>
                    </GalleryItem>
                  </Link>
                )}
              </ProgressiveImage>
            ))}
          </GalleryWrap>
        </Container>
      </FullScreenSection>
    </BasicLayout>
  )
}

export const getStaticPaths = async () => {
  const dynasties = await getDynasties()
  const paths =
    dynasties?.map(dynasty => {
      return { params: { slug: dynasty.slug || '' } }
    }) || []
  return {
    fallback: false,
    paths,
  }
}

export const getStaticProps = async ({ params }) => {
  const { slug } = params
  const dynasty = await getDynasties(`_slug_eq=${slug}`)
  const siteSettings = await getSiteSettings()
  const biomes = dynasty[0]?.biomesList?.map(data => data.biome)
  const dynastiesList = await getDynasties()

  return {
    props: {
      data: {
        biomes: biomes || null,
        dynastiesList: dynastiesList || null,
        dynasty: dynasty[0] || null,
        siteSettings: siteSettings || null,
        slug,
      },
    },
    revalidate: 300,
  }
}

export default Biome
